import React, {
  useState,
  useRef,
  ReactNode,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { JemGenContext } from "./jemGenContext";
import {
  Ard,
  CodeId,
  DescribeChartOfAccountsCommand,
  FuseAccountingCentralDatastoreClient,
  GetArdCommand,
  GetArdResponse,
  UseCaseInclusion,
  UseCase,
} from "@amzn/fuse-accounting-central-datastore-client";
import { InitDomain } from "../domain/domainContext";
import { ArdRegionMap, ModalMode } from "./types";
import { Domain } from "../../config/BartLambdaConfig";
import { Auth } from "aws-amplify";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";
import { isProd } from "src/util";

// Derive segment field names from CodeId.Visitor type (excludes the _ catch-all)
type SegmentField = Exclude<keyof CodeId.Visitor<void>, "_">;
const SEGMENT_FIELDS: SegmentField[] = [
  "companyCode",
  "location",
  "costCenter",
  "accountNumber",
  "productLine",
  "channel",
  "project",
];

/**
 * Extracts unique segment CodeIds from an array of use cases.
 * @param usecases Array of UseCase objects to extract segments from
 * @param existingKeys Optional set of keys to exclude (for incremental updates)
 * @returns Map of segment key to CodeId object
 */
const extractSegmentCodeIds = (
  usecases: UseCase[] | undefined,
  existingKeys?: Set<string>,
): Map<string, CodeId> => {
  const codeIdsMap = new Map<string, CodeId>();

  usecases?.forEach((useCase) => {
    useCase.uslLines?.forEach((uslLine) => {
      SEGMENT_FIELDS.forEach((field) => {
        const value = uslLine[field];
        if (value) {
          const key = `${field}:${value}`;
          if (
            !codeIdsMap.has(key) &&
            (!existingKeys || !existingKeys.has(key))
          ) {
            codeIdsMap.set(key, { [field]: value } as unknown as CodeId);
          }
        }
      });
    });
  });

  return codeIdsMap;
};

/**
 * Builds a descriptions map from the DescribeChartOfAccountsCommand response.
 * @param descriptions The descriptions object from the API response
 * @returns Map of segment key to codeName
 */
const buildDescriptionsMap = (
  descriptions: Record<string, { codeName?: string }> | undefined,
): Map<string, string> => {
  const descriptionsMap = new Map<string, string>();
  if (descriptions) {
    Object.entries(descriptions).forEach(([key, description]) => {
      if (description?.codeName) {
        descriptionsMap.set(key, description.codeName);
      }
    });
  }
  return descriptionsMap;
};

interface JemGenContextProviderProps {
  children: ReactNode;
}

export const JemGenContextProvider: React.FC<JemGenContextProviderProps> = ({
  children,
}) => {
  const [selectedArd, setSelectedArd] = useState<Ard | null>(null);
  const [selectedArdDetailed, setSelectedArdDetailed] =
    useState<GetArdResponse | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.CREATE);
  const [existingArds, setExistingArds] = useState<Ard[]>([]);
  const [toolsContentSelection, setToolsContentSelection] = useState<
    string | null
  >(null);
  const [coaDescriptions, setCoaDescriptions] = useState<Map<string, string>>(
    new Map(),
  );
  const [isLoadingCoaDescriptions, setIsLoadingCoaDescriptions] =
    useState(false);
  const ardRegionMapRef = useRef<ArdRegionMap>(new Map());
  const facdClientMapRef = useRef(
    new Map<string, FuseAccountingCentralDatastoreClient>(),
  );
  // Track which COA keys have been queried to avoid infinite loops and duplicate requests
  const queriedCoaKeysRef = useRef<Set<string>>(new Set());

  const domainData = useContext(InitDomain);

  const getFacdClient = async (
    domain: Domain,
  ): Promise<FuseAccountingCentralDatastoreClient | null> => {
    const existingClient = facdClientMapRef.current.get(domain);
    if (existingClient) {
      return existingClient;
    }

    // Create new client if it doesn't exist
    try {
      const credentials = await Auth.currentCredentials();
      const essentialCredentials = Auth.essentialCredentials(credentials);
      const newClient = new FuseAccountingCentralDatastoreClient({
        ...getFacdConfig(domain),
        credentials: essentialCredentials,
      });

      facdClientMapRef.current.set(domain, newClient);

      // Set timeout to delete the client after 30 minutes (1800000 ms)
      setTimeout(
        () => {
          facdClientMapRef.current.delete(domain);
        },
        30 * 60 * 1000,
      );

      return newClient;
    } catch (error) {
      console.error(`Error creating FACD client for domain ${domain}:`, error);
      return null;
    }
  };

  const setArdRegionMap = (map: ArdRegionMap) => {
    ardRegionMapRef.current = map;
  };

  // Fetch ARD data function moved from ard-edit-modal
  const fetchArdData = async (ardId: string) => {
    setSelectedArdDetailed(null);
    try {
      const ardRegion = ardRegionMapRef.current.get(ardId);
      if (ardRegion === undefined) {
        throw Error(`Unknown region for ARD ${ardId}`);
      }
      const facdClient = await getFacdClient(ardRegion);

      if (facdClient) {
        const getArdCommand = new GetArdCommand({
          ardId: ardId,
          useCaseInclusion: UseCaseInclusion.ALL,
          withHistory: true,
        });

        const response = await facdClient.send(getArdCommand);

        if (response && response.ard) {
          setSelectedArdDetailed(response);
        }
      } else {
        console.error("No FACD client found for region:", ardRegion);
      }
    } catch (error) {
      console.error("Error fetching ARD data for selectedArdDetailed:", error);
    }
  };

  // Automatically fetch detailed ARD data when selectedArd changes
  useEffect(() => {
    if (selectedArd?.id) {
      console.log(
        "SelectedArd changed, fetching detailed data:",
        selectedArd.id,
      );
      setSelectedArdDetailed(null);
      fetchArdData(selectedArd.id);
    } else {
      console.log("No selectedArd ID, clearing selectedArdDetailed");
      setSelectedArdDetailed(null);
    }
  }, [selectedArd?.id, domainData.domain, existingArds]);

  /**
   * Fetches COA descriptions for the given CodeIds and updates the state.
   * @param codeIdsMap Map of segment keys to CodeId objects to fetch
   * @param mergeWithExisting If true, merges new descriptions with existing state; if false, replaces entirely
   */
  const fetchAndSetCoaDescriptions = useCallback(
    async (codeIdsMap: Map<string, CodeId>, mergeWithExisting: boolean) => {
      if (codeIdsMap.size === 0) {
        if (!mergeWithExisting) {
          setCoaDescriptions(new Map());
        }
        return;
      }

      setIsLoadingCoaDescriptions(true);

      try {
        const region = isProd() ? "prod-FE" : "devo-FE";
        const facdClient = await getFacdClient(region);

        if (!facdClient) {
          console.error(`Failed to get FACD client for region '${region}'`);
          if (!mergeWithExisting) {
            setCoaDescriptions(new Map());
          }
          return;
        }

        const codeIds = Array.from(codeIdsMap.values());
        const command = new DescribeChartOfAccountsCommand({ codeIds });
        const response = await facdClient.send(command);

        const newDescriptions = buildDescriptionsMap(response.descriptions);

        if (mergeWithExisting) {
          // Use functional update to merge with latest state (avoids stale closure)
          setCoaDescriptions((prev) => {
            const merged = new Map(prev);
            newDescriptions.forEach((value, key) => {
              merged.set(key, value);
            });
            return merged;
          });
        } else {
          setCoaDescriptions(newDescriptions);
        }
      } catch (error) {
        console.error("Failed to fetch COA descriptions:", error);
        if (!mergeWithExisting) {
          setCoaDescriptions(new Map());
        }
      } finally {
        setIsLoadingCoaDescriptions(false);
      }
    },
    [],
  );

  // Fetch COA descriptions for all 7 segments when existingArds changes
  useEffect(() => {
    const codeIdsMap = new Map<string, CodeId>();
    existingArds.forEach((ard) => {
      const ardCodeIds = extractSegmentCodeIds(ard.usecases);
      ardCodeIds.forEach((value, key) => {
        if (!codeIdsMap.has(key)) {
          codeIdsMap.set(key, value);
        }
      });
    });

    // Reset tracked keys since we're replacing all descriptions
    queriedCoaKeysRef.current = new Set(codeIdsMap.keys());
    fetchAndSetCoaDescriptions(codeIdsMap, false);
  }, [existingArds, fetchAndSetCoaDescriptions]);

  // Fetch COA descriptions for new segments when selectedArdDetailed changes
  useEffect(() => {
    if (!selectedArdDetailed?.ard?.usecases) {
      return;
    }

    // Extract segment values not already queried (using ref to avoid infinite loop)
    const newCodeIds = extractSegmentCodeIds(
      selectedArdDetailed.ard.usecases,
      queriedCoaKeysRef.current,
    );

    if (newCodeIds.size > 0) {
      // Track queried keys in ref BEFORE fetch to prevent duplicate requests
      newCodeIds.forEach((_, key) => queriedCoaKeysRef.current.add(key));
      fetchAndSetCoaDescriptions(newCodeIds, true);
    }
  }, [selectedArdDetailed, fetchAndSetCoaDescriptions]);

  return (
    <JemGenContext.Provider
      value={{
        existingArds,
        setExistingArds,
        selectedArd,
        setSelectedArd,
        selectedArdDetailed,
        setSelectedArdDetailed,
        getFacdClient,
        ardRegionMap: ardRegionMapRef.current,
        setArdRegionMap,
        modalMode,
        setModalMode,
        toolsContentSelection,
        setToolsContentSelection,
        coaDescriptions,
        isLoadingCoaDescriptions,
      }}
    >
      {children}
    </JemGenContext.Provider>
  );
};
